import type { Proposal, ProposalDelta } from './ambient-types';
import type { ProposalOrigin } from './panel-types';

/**
 * Matching a re-analysis run's `ProposalDelta[]` back onto proposals (PROD-1979).
 *
 * This file exists because the matching rule is the part that can hurt a patient. A delta says
 * "this finding is gone from the new run"; if that lands on the wrong card, the clinician is
 * asked to reconsider a diagnosis the model never withdrew. So the identity used here is the
 * SERVER'S identity — its normalised code — reproduced exactly, and never the array position or
 * the display label:
 *
 *   - position is meaningless across runs. `to-proposals` ids are positional (`diagnosis-0`), so
 *     a run that drops one diagnosis renumbers every later one. Zipping `deltas[i]` to
 *     `proposals[i]` would silently shift every label by one.
 *   - the label is unstable BY DESIGN. The server keys a diagnosis by ICD code precisely so a
 *     reworded finding ("Headache" → "Headache, unspecified") reads as unchanged rather than as
 *     a withdrawal plus a new proposal. Matching on the label throws that away and manufactures
 *     the spurious Keep/Remove prompt the code-keying exists to prevent.
 *
 * The normalisation mirrors `ClinicalReanalysisService.Norm` / `NormTokens`. Kept in sync by
 * hand, like the server's own copy of the post-stop budget: there is no shared artifact between
 * a C# service and a TS client, and the alternative — matching loosely and hoping — is how a
 * withdrawal lands on the wrong row.
 */

/**
 * Mode 8 says `radiology`; the domain `Proposal.kind` says `imaging` (the DS canvas's word).
 * `to-proposals` already performs that translation one way, and a delta arrives in the server's
 * vocabulary, so it has to be translated back or every imaging delta silently fails to match.
 */
const DELTA_KIND: Record<Proposal['kind'], string> = {
  diagnosis: 'diagnosis',
  medication: 'medication',
  lab: 'lab',
  imaging: 'radiology',
  vaccination: 'vaccination',
  // PROD-3179. Video sends no procedure deltas yet, so this kind never matches one. It is still the
  // decision record's `proposalKind`, through `decisionIdentityOf`.
  procedure: 'procedure',
};

/** `Norm`: trim + lower-case, and treat blank as no identity at all. */
function norm(value: string | undefined | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/**
 * `NormTokens`: lower-case, split on anything that is not a letter or digit, sort, rejoin.
 * "right knee" and "knee, right" collapse to one key; "knee" and "shoulder" do not.
 *
 * `\p{L}\p{N}` rather than `a-z0-9` because the server splits on `char.IsLetterOrDigit`, which is
 * Unicode-aware. An ASCII-only class would reduce an Arabic body area to the empty key and make
 * every such order match every other one.
 */
function normTokens(value: string): string | null {
  const tokens = value.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (tokens.length === 0) return null;
  // Default `sort()` compares UTF-16 code units, which is what StringComparer.Ordinal does.
  return tokens.sort().join(' ');
}

/**
 * The server's identity key for a proposal, or null when the proposal carries nothing to
 * identify it by (the server skips blank keys too, so an unkeyable proposal simply never
 * matches a delta rather than matching all of them).
 */
export function proposalKey(proposal: Proposal): string | null {
  switch (proposal.kind) {
    case 'diagnosis':
      // Code first, name as the fallback — the same precedence the server uses, and the reason a
      // reworded diagnosis is recognised as the same finding.
      return norm(proposal.source.icdCode?.trim() ? proposal.source.icdCode : proposal.source.name);
    case 'imaging':
      // No name and no code on a radiology order, so modality plus body area is the identity.
      return normTokens(`${proposal.source.modality ?? ''} ${proposal.source.bodyArea ?? ''}`);
    default:
      return norm(proposal.source.name);
  }
}

/**
 * Kind and key together. A lab and a diagnosis may normalise alike; they are not the same row.
 *
 * A space joins them, which is unambiguous because every kind is a single word from a fixed set —
 * the id is only ever compared, never split back apart.
 */
export function deltaId(kind: string, key: string | null | undefined): string | null {
  const normalised = norm(key);
  return normalised ? `${kind} ${normalised}` : null;
}

/** `deltaId` for a proposal, in the server's kind vocabulary. */
export function proposalDeltaId(proposal: Proposal): string | null {
  return deltaId(DELTA_KIND[proposal.kind], proposalKey(proposal));
}

/** The three standings the SERVER can report. `firstPass` and `kept` are ours, not its. */
export type WireOrigin = Extract<ProposalOrigin, 'new' | 'unchanged' | 'withdrawn'>;

/**
 * Declaration order of the C# `ProposalOrigin` enum. Load-bearing: see below.
 */
const WIRE_ORIGINS: readonly WireOrigin[] = ['new', 'unchanged', 'withdrawn'];

/**
 * Reads a delta's origin off the wire, accepting BOTH the string name and the ordinal.
 *
 * Not defensive padding. `ProposalOrigin` in ReanalysisModels.cs carries no
 * `[JsonConverter(typeof(JsonStringEnumConverter))]` — unlike `TranscriptMessageKind` and
 * `AmbientScribeMode`, which both do — and Scrips.VideoApplication registers no global string
 * converter (`services.AddControllers()`, no `AddJsonOptions`). So System.Text.Json serialises
 * this one enum as its ORDINAL: `"origin": 2` for Withdrawn, not `"origin": "Withdrawn"`.
 *
 * A client that compared against the string would find no withdrawals at all — the exact silent
 * miss that matters most, because the clinician would then never be told an accepted finding
 * lost its support. Both forms are read so that adding the missing attribute server-side is a
 * safe change here rather than a breaking one, and so this file does not have to guess which
 * shape is live.
 *
 * Unknown values return undefined rather than a default. Guessing "unchanged" would silently
 * suppress a real withdrawal; guessing "withdrawn" would invent one.
 */
export function parseDeltaOrigin(origin: unknown): WireOrigin | undefined {
  if (typeof origin === 'number') return WIRE_ORIGINS[origin];
  if (typeof origin === 'string') {
    const lowered = origin.trim().toLowerCase();
    return WIRE_ORIGINS.find((known) => known === lowered);
  }
  return undefined;
}

/** Index of `deltaId` → standing, for the run just returned. */
export function indexDeltas(deltas: readonly ProposalDelta[] | undefined): Map<string, WireOrigin> {
  const index = new Map<string, WireOrigin>();
  for (const delta of deltas ?? []) {
    const id = deltaId(delta.kind, delta.key);
    const origin = parseDeltaOrigin(delta.origin);
    if (id && origin) index.set(id, origin);
  }
  return index;
}

/**
 * The reason shown on a flagged card.
 *
 * Worded as an instruction to CHECK, not as a statement that the record changed, because nothing
 * has changed: re-analysis never writes to and never removes from the Assessment & Plan (D12).
 * It also does not claim the item is in the A&P — the panel observes the clinician pressing
 * accept, not the A&P write completing, and an accept that opened a seeded search may have been
 * abandoned. "You accepted this" is true either way.
 */
export const WITHDRAWN_FLAG_REASON =
  'The corrected transcript no longer supports this. You accepted it earlier — keep it, or ' +
  'remove it in the Assessment & Plan.';

export interface ReanalysisAnnotation {
  origin?: ProposalOrigin;
  /** Set only on an accepted finding the new run dropped. Absent once the clinician keeps it. */
  flagReason?: string;
}

export interface AnnotateInput {
  /** Proposals derived from the LATEST encounter document. */
  current: readonly Proposal[];
  /** Accepted findings the latest run dropped, carried over by `applyReanalysis`. */
  withdrawn: readonly Proposal[];
  deltas: readonly ProposalDelta[] | undefined;
  /** `deltaId`s the clinician has chosen to keep. Once kept, never re-flagged. */
  kept: ReadonlySet<string>;
}

/**
 * Per-proposal-id origin and flag, for the panel to lay onto the cards.
 *
 * Returns a map rather than mutated proposals so the callbacks a flag needs (`onKeep`) stay in
 * the container and this stays pure and testable.
 *
 * An empty `deltas` yields no origins at all, and that is the correct first-pass rendering: the
 * card treats a missing origin exactly as it treats `firstPass` (no tag). Labelling everything
 * `unchanged` because nothing was compared would be a claim the run did not make.
 */
export function annotateReanalysis({
  current,
  withdrawn,
  deltas,
  kept,
}: AnnotateInput): Map<string, ReanalysisAnnotation> {
  const index = indexDeltas(deltas);
  const annotations = new Map<string, ReanalysisAnnotation>();

  for (const proposal of current) {
    const id = proposalDeltaId(proposal);
    // A `withdrawn` standing on a proposal the current run still contains is a contradiction, so
    // it is ignored rather than rendered: the run that produced this proposal is the authority on
    // whether it exists.
    const origin = id ? index.get(id) : undefined;
    if (origin === 'new' || origin === 'unchanged') annotations.set(proposal.id, { origin });
  }

  for (const proposal of withdrawn) {
    const id = proposalDeltaId(proposal);
    if (id && kept.has(id)) {
      annotations.set(proposal.id, { origin: 'kept' });
      continue;
    }
    annotations.set(proposal.id, { origin: 'withdrawn', flagReason: WITHDRAWN_FLAG_REASON });
  }

  return annotations;
}
